import PropTypes from 'prop-types'
import React, { useLayoutEffect } from 'react'

// component stack
import * as Styled from './Filters.styles.jsx'
import { Filter } from 'components/molecules'
import {
  Checkbox,
  FilterAccordion,
  FilterModal,
} from 'components/molecules/Filter/partials'
import { Divider, Select, Tooltip } from 'components/atoms'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'

// redux && libs
import { useSelector, useDispatch } from 'react-redux'
import {
  setFilterSelection,
  resetFilterCollection,
  updateFilterInCollection,
} from 'redux/actions/filterActions'
import { withRouter } from 'react-router-dom'
import { useMobileSwitch } from 'hooks'
import { FilterAccordionItem } from 'components/molecules/Filter/partials/FilterAccordion/FilterAccordion.styles'
import {
  searchRecursiveForDuplicate,
  updateOpeningTimesFilter,
} from 'settings/app/helpers'
import { entryPointId } from '../../../../settings/app/constants'

/**
 * filter mapper
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Filters = (props) => {
  const {
    isLoading,
    setLoadingState,
    history,
    isFilterFullscreen,
    closeFilter,
    showSortOnMobile,
    resultContainerRef,
    typoHeaderHeight,
    entryPoint,
  } = props

  // max number of filter options to show within filter groups
  // until 'load more' button is displayed
  const maxNumOptions = 5

  // handle filter (group) accordion open state
  const [openFilterGroup, setOpenFilterGroup] = React.useState('')
  const [openSorts, setOpenSorts] = React.useState(false)
  const textContext = React.useContext(TextContext)
  const isMobile = useMobileSwitch()
  const dispatch = useDispatch()

  const { filter } = useSelector((state) => state)
  const { collection: filterCollection, sorting, activeSort } = filter

  // handles filter group accordion "show all filter options" state
  const [showAllOptions, setShowAllOptions] = React.useState(false)
  // handles count of filter options to show
  const [optionsCount, setOptionsCount] = React.useState(maxNumOptions)

  // mobile filter (this has no relevance for desktop filters)
  const [mobileSort, setMobileSort] = React.useState(activeSort)
  const [mobileFilters, setMobileFilters] = React.useState(filterCollection)
  const [openTimesDay, setOpenTimesDay] = React.useState(null)
  const [openTimesHours, setOpenTimesHours] = React.useState(null)

  // temp state for quality indicators
  const [indicatorFilters, setIndicatorFilter] = React.useState()
  // hold initial filter (if user edit filter and reset them)
  const [initialIndicatorFilterSet, setInitialIndicatorFilterSet] =
    React.useState()

  /**
   * set initial value (redux state) for TimeFilter (Selects)
   */
  useLayoutEffect(() => {
    // time filter
    if (entryPointId.DOCTORS === entryPoint) {
      const timeFilterObject = filterCollection.filter(
        (filter) => 'custom_opening_hours' === filter.key
      )
      if (0 < timeFilterObject.length) {
        if ('' !== timeFilterObject[0].options[0].selected) {
          setOpenTimesDay(timeFilterObject[0].options[0].selected)
        }
        if ('' !== timeFilterObject[0].options[1].selected) {
          setOpenTimesHours(timeFilterObject[0].options[1].selected)
        }
      }
    }

    // check for quality indicator filters
    if (entryPointId.CAREHOMES === entryPoint) {
      // check if quality indicator filters provided
      const qualityFilterObject = getModalFilterObject()

      if (qualityFilterObject) {
        setIndicatorFilter(qualityFilterObject)
      }
    }
  }, [filterCollection, entryPoint])

  /**
   *  Prevent page scrolling when modal is open
   */
  React.useEffect(() => {
    if (isMobile) {
      document.body.style.top = `-${ window.scrollY }px`
      document.body.style.position = 'fixed'

      return () => {
        const scrollY = document.body.style.top
        document.body.style.position = ''
        document.body.style.top = ''
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
      }
    }
  }, [isMobile])

  /**
   * return filter object by key
   * @param key
   * @returns {*}
   */
  const getFilterByKey = (key) =>
    filterCollection.find((filter) => key === filter.key)

  /**
   * handler for time selection @ filter
   * @param e
   * @param filter
   */
  const handleOpenTimesFilterChange = (e, filter) => {
    if ('opening_time' === filter) {
      setOpenTimesHours(e)
    } else {
      setOpenTimesDay(e)
    }

    /** handle Filter change if timeFilter has valid redux state
     * (true => user has filtered before) => refresh filter results
     **/
    if (
      0 <
      getFilterByKey('custom_opening_hours').options.filter(
        (option) => '' !== option.selected
      ).length
    ) {
      const timeFilterValues = {
        opening_day: openTimesDay,
        opening_time: openTimesHours,
      }
      if ('opening_time' === filter) {
        timeFilterValues.opening_time = e
      } else {
        timeFilterValues.opening_day = e
      }

      handleFilterSelect({
        filter: 'custom_opening_hours',
        parentKey: false,
        timeFilterValues,
      })
    }
  }

  const getModalFilterObject = () =>
    filterCollection.find((filter) => 'modal' === filter.type)

  /**
   * handler for modal filters reset
   */
  const handleModalFilterReset = () => {
    const qualityFilterObject = getModalFilterObject()

    const updatedQualityFilterObject = {
      ...qualityFilterObject,
      tabs: qualityFilterObject.tabs.map((tab) => ({
        ...tab,
        options: tab.options.map((option) => ({
          ...option,
          options: option.options.map((subOption) => ({
            ...subOption,
            selected: false,
          })),
        })),
      })),
    }

    if (qualityFilterObject) {
      setIndicatorFilter(updatedQualityFilterObject)
    }
  }

  /**
   * handler for allying a set of modal filters
   */
  const handleModalFilterApply = () => {
    setLoadingState(true)
    dispatch(
      setFilterSelection('modalFilter', { indicatorFilters }, (url) => {
        scrollToResultListTop()
        history.push(url)
        setLoadingState(false)
      })
    )
  }

  /**
   * map all kinds of filters to their components
   * @returns {unknown[]} (component stack)
   */
  const getFilters = () => {
    // mobile => manipulating only local states until filters will apply
    // desktop => each selection will manipulate the redux state and fire a xhr
    const filters = isMobile ? mobileFilters : filterCollection
    let lastGroupFilter = filters.filter((filter) => filter.type === 'group')

    if (lastGroupFilter.length > 0) {
      lastGroupFilter = lastGroupFilter[lastGroupFilter.length - 1].key
    }

    return Object.keys(filters).map((item, index) => {
      const filter = filters[index]

      /**
       * set modal filters
       * important, user have to accept filters before the filters will send to api
       * same handling like mobile filters
       */
      if ('modal' === filter.type && indicatorFilters) {
        let numberOfSelectedFilters = 0

        indicatorFilters.tabs.forEach((tab) => {
          tab.options.forEach((option) => {
            option.options.forEach((subOption) => {
              if (subOption.selected) {
                numberOfSelectedFilters++
              }
            })
          })
        })

        return (
          <FilterModal
            key={ index }
            filter={ indicatorFilters }
            checked={ numberOfSelectedFilters > 0 }
            onChange={ (updatedFilter) => setIndicatorFilter(updatedFilter) }
            onCustomClose={ () => handleModalFilterReset() }
            onCustomApply={ () => handleModalFilterApply() }
            onCustomModalOpen={ (modalOpenState) => {
              if (modalOpenState) {
                setInitialIndicatorFilterSet(getModalFilterObject())
              } else {
                setIndicatorFilter(initialIndicatorFilterSet)
              }
            } }
          />
        )
      }

      // single filter option
      if ('boolean' === filter.type) {
        return getCheckboxFilter({
          filter,
          index,
          parentKey:
            filter.isDuplicate !== null
              ? searchRecursiveForDuplicate(filters, filter.key, {
                  parentKey: false,
                  index,
                })
              : false,
        })
      }

      // special case (currently only midwives)
      if ('splitted_group' === filter.type) {
        return (
          <div className='splitted-group' key={ index }>
            <div className='label'>{filter.label}</div>
            <div className='splitted-group-filters'>
              {filter.options.map((filterOption, filterOptionIndex) =>
                getCheckboxFilter({
                  filter: filterOption,
                  index: filterOptionIndex,
                  parentKey: filter.key,
                })
              )}
            </div>
          </div>
        )
      }

      // grouped filter options (length compare only to avoid rendering empty groups [dev only])
      if ('group' === filter.type && 0 < Object.keys(filter.options).length) {
        let accordionHasCheckedFilters = false

        if ('custom_opening_hours' === filter.key) {
          accordionHasCheckedFilters =
            '' !== filter.options[0].selected &&
            '' !== filter.options[1].selected
        } else {
          accordionHasCheckedFilters =
            0 <
            filter.options.filter((option) => option.selected === true).length
        }

        return (
          <FilterAccordion
            key={ index }
            id={ `${ filter.key }-group` }
            title={ filter.label }
            open={ openFilterGroup === filter.label }
            handleOpen={ () => onHandleOpenAccordion(index) }
            checked={ accordionHasCheckedFilters }
            entryCount={ filterCollection[index].options.length }
            loadMore={ showAllOptions }
            className={
              filter.key === lastGroupFilter ? 'padding-bottom' : null
            }>
            {'custom_opening_hours' === filter.key ? (
              <>
                <div className='opening-times-day'>
                  {getCheckboxFilter({
                    filter,
                    index,
                    parentKey: false,
                  })}
                </div>
                {filter.options.map((optionsArr, optionsIndex) => {
                  if ('opening_time' === optionsArr.key) {
                    return (
                      <div className='select-time' key={ optionsIndex }>
                        {textContext['filter.container.label.time']}{' '}
                        {getFilterSelect({ filter: optionsArr })}
                      </div>
                    )
                  }
                  return (
                    <div className='select-day' key={ optionsIndex }>
                      {textContext['filter.container.label.day']}
                      {getFilterSelect({ filter: optionsArr })}
                    </div>
                  )
                })}
              </>
            ) : (
              filter.options
                .slice(0, optionsCount)
                .map((filterOption, filterOptionIndex) =>
                  getCheckboxFilter({
                    filter: filterOption,
                    index: parseInt(index, 10) * 10 + filterOptionIndex,
                    parentKey: filter.key,
                  })
                )
            )}

            {!showAllOptions &&
              filters[index].options.length > maxNumOptions && (
                <Styled.LoadMoreButton
                  id={ `filter-load-more-btn-${ filter.key }` }
                  className='btn-moreFilters'
                  data-cy='load-more-filters-btn'
                  text={ `${ filter.options.length - maxNumOptions } ${
                    textContext['filterAccordion.loadMoreFilters']
                  }` }
                  onClick={ () =>
                    onLoadMoreOptionsClicked(filter.options.length)
                  }
                />
              )}
          </FilterAccordion>
        )
      }
      return false
    })
  }

  /**
   * return a mapped component for selects
   * @param filter
   * @returns {*}
   */
  const getFilterSelect = ({ filter }) => (
    <Select
      id={ filter.key }
      placeholder={
        'opening_day' === filter.key
          ? textContext['filter.openingTimes.day']
          : textContext['filter.openingTimes.time']
      }
      value={ 'opening_day' === filter.key ? openTimesDay : openTimesHours }
      handleSelect={ (e) => handleOpenTimesFilterChange(e, filter.key) }
      options={ Object.keys(filter.options).map((index) => ({
        value: index,
        option: filter.options[index],
      })) }
    />
  )

  const onLoadMoreOptionsClicked = (newOptionsCount) => {
    setShowAllOptions(true)
    setOptionsCount(newOptionsCount)
  }

  const onHandleOpenAccordion = (index) => {
    // if accordion gets opened
    if (openFilterGroup !== filterCollection[index].label) {
      setOpenFilterGroup(filterCollection[index].label)
      setShowAllOptions(false)
      setOptionsCount(maxNumOptions)
    } else {
      setOpenFilterGroup('')
    }
  }

  /**
   * return a checkbox filter component
   * with device depending on handler
   * @param filter (key)
   * @param index
   * @param parentKey
   * @returns {*}
   */
  const getCheckboxFilter = ({ filter, index, parentKey }) => {
    let isDisabled = isLoading
    let isChecked = filter.selected

    // time filter handling
    if ('custom_opening_hours' === filter.key) {
      isDisabled =
        isLoading ||
        null === openTimesDay ||
        null === openTimesHours ||
        '' === openTimesDay ||
        '' === openTimesHours
      isChecked =
        '' !== filter.options[0].selected && '' !== filter.options[1].selected
    }

    // do not render checkbox without label
    if (!filter.label) {
      const filtersWithoutLabel = sessionStorage.getItem('filtersWithoutLabel')
      if (!filtersWithoutLabel?.split(',').includes(filter.key)) {
        console.error(`Label for filter "${ filter.key }" is missing`)
        sessionStorage.setItem(
          'filtersWithoutLabel',
          filtersWithoutLabel
            ? `${ filtersWithoutLabel },${ filter.key }`
            : filter.key
        )
      }
      return null
    }

    return (
      <Checkbox
        key={ index }
        id={
          parentKey
            ? `${ filter.key }-option-${ parentKey }`
            : `${ filter.key }-option`
        }
        data-cy={ filter.key }
        value={ filter.label }
        checked={ isChecked }
        handleChange={ () => {
          isMobile
            ? updateFilterUiStates({ filter: filter.key, parentKey })
            : handleFilterSelect({ filter: filter.key, parentKey })
        } }
        disabled={ isDisabled }>
        <>
          {'custom_opening_hours' === filter.key
            ? textContext['filter.openingTimes.label']
            : filter.label}
          {null !== filter.tip && undefined !== filter.tip && (
            <Tooltip
              dialogTitle={ filter.label }
              infoIconId={ `${ filter.label }-icon-id` }
              tooltipId={ `${ filter.label }-tip-id` }
              arrow='left'>
              {filter.tip}
            </Tooltip>
          )}
        </>
      </Checkbox>
    )
  }

  /**
   * set new filters without sending a request
   * case => mobile devices
   * @param filter
   * @param parentKey
   */
  const updateFilterUiStates = ({ filter, parentKey }) => {
    let updatedCollection
    if ('custom_opening_hours' === filter) {
      updatedCollection = updateOpeningTimesFilter(mobileFilters, {
        filter,
        parentKey: false,
        opening_day: openTimesDay,
        opening_time: openTimesHours,
      })
    } else {
      updatedCollection = updateFilterInCollection(mobileFilters, {
        key: filter,
        parentKey,
      })
    }
    setMobileFilters(updatedCollection)
  }

  /**
   * another scroll handler
   */
  const scrollToResultListTop = () => {
    const resConRefRect = resultContainerRef.current.getBoundingClientRect()

    let scrollPos
    if (0 > resConRefRect.top && !isMobile) {
      scrollPos = resConRefRect.top + window.scrollY - typoHeaderHeight - 10
    } else if (isMobile) {
      scrollPos = resConRefRect.top + window.scrollY - 60
    }

    window.scroll({
      behavior: 'smooth',
      top: scrollPos,
    })
  }

  /**
   * perform a search request based on mobile filter changes
   * case => mobile devices
   */
  const searchWithFilterNewCollection = () => {
    closeFilter()
    setLoadingState(true)

    dispatch(
      setFilterSelection(
        'mobileFilter',
        {
          mobileSort,
          mobileFilters,
          indicatorFilters,
        },
        (url) => {
          scrollToResultListTop()
          history.push(url)
          setLoadingState(false)
        }
      )
    )
  }

  /**
   * set selected sort key to component state && close sort accordion
   * case => mobile only
   * @param sortKey
   */
  const handleMobileSortChange = (sortKey) => {
    setMobileSort(sortKey)
    setOpenSorts(!openSorts)
  }

  /**
   * filter handler && url manipulation
   * @param filter
   * @param parentKey
   * @param timeFilterValues
   */
  const handleFilterSelect = ({
    filter,
    parentKey = false,
    timeFilterValues = false,
  }) => {
    let filterObject = { filter, parentKey }

    // pass user selection for opening times
    if ('custom_opening_hours' === filter) {
      // initial set || update case
      if (false !== timeFilterValues) {
        filterObject = { ...filterObject, ...timeFilterValues }
      } else {
        filterObject = {
          ...filterObject,
          opening_day: openTimesDay,
          opening_time: openTimesHours,
        }
      }
    }

    setLoadingState(true)
    dispatch(
      setFilterSelection('desktopFilter', filterObject, (url) => {
        scrollToResultListTop()
        setLoadingState(false)
        history.push(url)
      })
    )
  }

  /**
   * * filter reset && url manipulation
   */
  const handleFilterReset = () => {
    setLoadingState(true)
    dispatch(
      resetFilterCollection((url) => {
        history.push(url)
        setLoadingState(false)
      })
    )
  }

  /**
   * iterate through filters to check if one filter is set
   * @returns {bool}
   */
  const isFilterSet = () => {
    const filters = filterCollection
    let isSet = false

    Object.keys(filters).forEach((item, index) => {
      // skip if already found one filter set was set
      if (isSet === true) return
      const filter = filters[index]

      if ('boolean' === filter.type) {
        if (filter.selected) {
          isSet = true
        }
      } else if (
        ('group' === filter.type || 'splitted_group' === filter.type) &&
        0 < filter.options.length
      ) {
        if ('custom_opening_hours' === filter.key) {
          if (
            '' !== filter.options[0].selected &&
            '' !== filter.options[1].selected
          ) {
            isSet = true
          }
        } else {
          filter.options.forEach((groupedFilter) => {
            if (groupedFilter.selected) {
              isSet = true
            }
          })
        }
      }
    })

    return isSet
  }

  return (
    <Styled.FilterContainer>
      <Filter
        id='filter'
        data-cy='filter'
        onReset={ () => {
          !isLoading && handleFilterReset()
        } }
        isFilterFullscreen={ isFilterFullscreen }
        closeFilter={ () => closeFilter() }
        onClickSearchBtn={ () => searchWithFilterNewCollection() }
        isLoading={ isLoading }
        isFilterSet={ isFilterSet() }>
        <>
          {/* mobile filters */}
          {isMobile && (
            <>
              {showSortOnMobile && (
                <>
                  <div className='label-mobile'>
                    {textContext['filter.sortLabel']}
                  </div>

                  <FilterAccordion
                    id={ 'sort' }
                    title={
                      sorting.find(
                        (sortOption) => sortOption.key === mobileSort
                      ).label
                    }
                    open={ openSorts }
                    handleOpen={ () => setOpenSorts(!openSorts) }
                    variant='select'>
                    {sorting.map((sortOption, key) => (
                      <FilterAccordionItem
                        key={ key }
                        onClick={ () => handleMobileSortChange(sortOption.key) }
                        title={ sortOption.label }>
                        {sortOption.label}
                        {'undefined' !== typeof sortOption.tip &&
                          null !== sortOption.tip && (
                            <>
                              <Tooltip
                                arrow='left'
                                dialogTitle={ sortOption.label }
                                tooltipId={ `${ sortOption.key }-tip-id` }
                                infoIconId={ `${ sortOption.key }-info-icon` }
                                className='tooltip'>
                                {sortOption.tip}
                              </Tooltip>
                            </>
                          )}
                      </FilterAccordionItem>
                    ))}
                  </FilterAccordion>

                  <Divider margin={ 16 } />
                </>
              )}

              {filterCollection &&
                Array.isArray(filterCollection) &&
                filterCollection.length > 0 && (
                  <div className='label-mobile'>
                    {textContext['filter.filterLabel.mobile']}
                  </div>
                )}
            </>
          )}
          {/* desktop filters */}
          {getFilters()}
        </>
      </Filter>
    </Styled.FilterContainer>
  )
}

Filters.defaultProps = {
  isLoading: false,
  isFilterFullscreen: false,
  closeFilter: false,
  showSortOnMobile: true,
}

Filters.propTypes = {
  /** flag for render map in fullScreen **/
  isLoading: PropTypes.bool,
  /** flag for render map in fullScreen **/
  isFilterFullscreen: PropTypes.bool,
  /** flag for render map in fullScreen **/
  closeFilter: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /** Handler to trigger loading change a parent **/
  setLoadingState: PropTypes.func.isRequired,
  // show or hide sorting options for mobile
  showSortOnMobile: PropTypes.bool,
  /** typ header height **/
  typoHeaderHeight: PropTypes.number,
  /** search typ or entity typ **/
  entryPoint: PropTypes.oneOf([
    'hospitals',
    'doctors',
    'midwives',
    'ambulances',
    'oncallservices', // health navigator
    'careservices',
    'carehomes',
    'caresupport',
    'dayandnightcare', // care navigator
  ]),
}

export default withRouter(Filters)
